import { reactive } from 'vue';
import type { IMenuGroup, IMenuSection } from "@fondationcss/ui";
import { STUDENT_SUBMISSIONS_FEATURE } from "@/utils/student-submission-utils";
import { STUDENT_REPORTS_FEATURE } from "@/utils/student-portal-features";
import { DEMONSTRATION_FEATURE } from "@/utils/demonstration-feature";
import { GROUP_TRAINING_FEATURE } from "@/utils/group-training-feature";

const MAX_SIDEBAR_GROUPS = 10;
export const STUDENT_STAGE_PATH = '/enseignement/mon-stage';
export const STUDENT_ASSIGNMENTS_PATH = '/enseignement/assignation-individuelle';
/** Where the student's section opens (#957) — read by the menu and by use-menu-section-guard. */
export const STUDENT_DASHBOARD_PATH = '/enseignement/mon-tableau-de-bord';
/** The anchor the runtime entries are inserted after — see afterScheduleIndex. */
export const STUDENT_SCHEDULE_PATH = '/enseignement/mon-horaire';

const studentStageMenuGroup: IMenuGroup = {
  title: 'Mon stage',
  icon: 'briefcase',
  to: STUDENT_STAGE_PATH,
  badge: 'beta',
};

// Keep the canonical path here. The legacy `assignations-individuelles` URL redirects to it,
// and Fondation uses this exact value to synchronize the active menu section.
const studentAssignmentsMenuGroup: IMenuGroup = {
  title: 'Assignations',
  icon: 'chalkboard-user',
  to: STUDENT_ASSIGNMENTS_PATH,
  badge: 'beta',
};

export const menus = reactive<IMenuSection[]>([
  {
    title: 'Élève',
    icon: 'user-graduate',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    permissions: ['Parcours.LearningModule.Élève'],
    groups: [
      // No possessive (#957): the section is already called « Élève », so « Mon calendrier » says
      // « mon » twice. « Rapports » closes the list, whatever is inserted above it.
      { title: 'Tableau de bord', icon: 'dashboard', to: STUDENT_DASHBOARD_PATH, },
      { title: 'Calendrier', icon: 'calendar', to: STUDENT_SCHEDULE_PATH, },
      { title: 'Examens', icon: 'file-circle-check', to: '/enseignement/mes-examens', },
      { title: 'Justificatifs', icon: 'file-medical', to: '/enseignement/mes-justificatifs', feature: STUDENT_SUBMISSIONS_FEATURE, },
      { title: 'Suivi', icon: 'note-sticky', to: '/enseignement/mon-suivi', },
      { title: 'Rapports', icon: 'file-lines', to: '/enseignement/mes-rapports', feature: STUDENT_REPORTS_FEATURE, },
    ],
  },
  {
    title: 'Enseignement',
    icon: 'chalkboard-user',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    permissions: ['Parcours.LearningModule.Enseignant'],
    groups: [
      { title: 'Tableau de bord', icon: 'dashboard', to: '/enseignement/tableau-de-bord', permissions: ['Parcours.LearningModule.Enseignant'], },
      { title: 'Calendrier', icon: 'calendar-days', to: '/enseignement/charge-annuelle', permissions: ['Parcours.LearningModule.Enseignant'], },
      { title: 'Présences', icon: 'hand', to: '/enseignement/presences', permissions: ['Parcours.LearningModule.Enseignant'], },
      { title: 'Examens', icon: 'file-circle-check', to: '/enseignement/examens', permissions: ['Parcours.LearningModule.Enseignant'] },
      {
        id: 'enseignement-assignations',
        label: 'Groupe',
        icon: 'chalkboard-user',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Enseignant'],
        // #812: the whole group sub-menu withdraws with the « Formation par groupes » flag.
        // On the GROUP, not the section: the section-index clamp of use-menu-section-guard
        // only counts sections by permissions, and must stay right.
        feature: GROUP_TRAINING_FEATURE,
        items: [
          {
            module: 'Parcours.LearningModule',
            items: [
              { title: 'Assiduité', icon: 'users', to: '/enseignement/assiduite' },
              { title: 'Progression', icon: 'chart-line', to: '/enseignement/progression-groupe', badge: 'beta' },
            ],
          },
        ],
      },
      {
        id: 'enseignement-individualise',
        label: 'Individualisé',
        icon: 'user',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Enseignant'],
        items: [
          {
            module: 'Parcours.LearningModule',
            items: [
              { title: 'Assignations', icon: 'chalkboard-user', to: '/enseignement/assignations' },
              { title: 'Vue consolidée', icon: 'table', to: '/enseignement/vue-consolidee' },
            ],
          },
        ],
      },
      { title: 'Rapports', icon: 'file-lines', to: '/enseignement/rapports', permissions: ['Parcours.LearningModule.Enseignant'] }
    ],
  },
  {
    title: 'Planification',
    icon: 'calendar',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    permissions: [
      'Parcours.LearningModule.EnseignementÉlargi',
      'Parcours.LearningModule.Administrateur',
      'Parcours.LearningModule.Superviseur',
      'Parcours.ATE.Administrateur',
      'Parcours.ATE.Enseignant',
      'Parcours.ATE.Superviseur',
      'Parcours.ATE.Visiteur',
    ],
    groups: [
      { title: 'Tableau de bord', icon: 'dashboard', to: '/planification/tableau-de-bord', },
      { title: 'Disponibilités par compétence', icon: 'calendar-check', to: '/planification/disponibilites',
        permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur', 'Parcours.LearningModule.EnseignementÉlargi'] },
      {
        id: 'planification-etudiants',
        label: 'Admission',
        icon: 'user-graduate',
        iconOnly: false,
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { title: 'Élèves', icon: 'user-group', to: '/planification/etudiants' },
              { title: 'Fréquentations', icon: 'user-graduate', to: '/planification/frequentations' },
            ],
          },
        ],
      },
      {
        id: 'planification-groupes',
        label: 'Groupes',
        icon: 'chalkboard',
        iconOnly: false,
        permissions: ['Parcours.LearningModule.Superviseur'],
        // #812 — same withdrawal as the Enseignement « Groupe » sub-menu above.
        feature: GROUP_TRAINING_FEATURE,
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { title: 'Groupes', icon: 'chalkboard', to: '/planification/vue-ensemble' },
              { title: 'Solveur', icon: 'wand-magic-sparkles', to: '/planification/simulation', badge: 'beta' },
            ],
          },
        ],
      },
      {
        title: 'Élèves individualisés', icon: 'user', to: '/planification/individualise',
      },
      {
        id: 'enseignement-ate',
        label: 'ATE & Stages',
        icon: 'briefcase',
        iconOnly: false,
        badge: 'beta',
        items: [
          {
            module: 'Parcours.ATE',
            items: [
              { title: 'Stages', icon: 'briefcase', to: '/planification/stages' },
              { title: 'Entreprises', icon: 'building', to: '/planification/entreprises' },
            ],
          },
        ],
      },
      { title: 'Rapports', icon: 'file-lines', to: '/planification/rapports' }
    ],
  },
  {
    title: 'Gestion',
    icon: 'users-gear',
    iconOnly: true,
    maxGroups: MAX_SIDEBAR_GROUPS,
    // The union of the permissions of the groups below. A permission missing here makes the
    // section invisible and therefore its screens unreachable, whatever the rights carried
    // further down — that is what deprived the administrators of sites, privacy, audit and
    // workflows of the whole section.
    permissions: [
      'Parcours.LearningModule.Administrateur',
      'Parcours.LearningModule.Superviseur',
      'Parcours.ATE.Administrateur',
      'Parcours.Locations.Administrateur',
      'Parcours.Demonstration.Administrateur',
      'Parcours.Demonstration.Concepteur',
      'Parcours.Demonstration.Démonstrateur',
      'Parcours.Demonstration.Observateur',
      'Core.Privacy.Administrateur',
      'Core.Audit.Administrateur',
      'Core.Audit.Consultation',
      'Core.Features.Manage',
      'Core.Workflow.Manage',
    ],
    groups: [
      { title: 'Tableau de bord', icon: 'dashboard', to: '/gestion/tableau-de-bord', module: 'Parcours.LearningModule', },
      {
        id: 'gestion-supervision',
        label: 'Supervision',
        icon: 'users-gear',
        iconOnly: false,
        // Each group carries the union of its blocks' permissions: a group all of whose items
        // are filtered out would still be displayed, folded onto "Vous n'avez accès à aucuns
        // modules."
        permissions: ['Parcours.LearningModule.Superviseur'],
        items: [
          {
            permissions: ['Parcours.LearningModule.Superviseur'],
            items: [
              { title: 'Enseignants', icon: 'chalkboard-user', to: '/gestion/enseignants' },
              { title: 'Embauches', icon: 'user-plus', to: '/gestion/embauches', badge: 'beta' },
            ],
          },
        ],
      },
      // What is taught: revised all year long, hence first.
      {
        id: 'gestion-pedagogie',
        label: 'Pédagogie',
        icon: 'graduation-cap',
        iconOnly: false,
        permissions: ['Parcours.ATE.Administrateur'],
        items: [
          {
            permissions: ['Parcours.ATE.Administrateur'],
            items: [
              { title: 'Programmes et Compétences', icon: 'box-archive', to: '/gestion/programmes' },
              { title: 'Gabarits', icon: 'layer-group', to: '/gestion/gabarits' },
              { title: 'Références', icon: 'star-half-stroke', to: '/gestion/references' },
            ],
          },
        ],
      },
      // Where and when it is taught: set at the start of the year, rarely touched afterwards.
      {
        id: 'gestion-etablissement',
        label: 'Établissement',
        icon: 'city',
        iconOnly: false,
        permissions: [
          'Parcours.Locations.Administrateur',
          'Parcours.ATE.Administrateur',
        ],
        items: [
          {
            permissions: ['Parcours.Locations.Administrateur'],
            items: [
              { title: 'Centres', icon: 'building', to: '/gestion/centres' },
              { title: 'Locaux et ressources', icon: 'door-open', to: '/gestion/locaux-ressources' },
              { title: "Lieux d'examen", icon: 'location-dot', to: '/gestion/lieux-examen' },
            ],
          },
          {
            permissions: ['Parcours.ATE.Administrateur'],
            items: [
              { title: 'Calendriers', icon: 'calendar', to: '/core/calendar' },
              { title: "Patrons d'horaire", icon: 'clock', to: '/core/schedule-patterns' },
            ],
          },
        ],
      },
      // Governance and cross-cutting tooling: opened occasionally, by distinct profiles.
      {
        id: 'gestion-systeme',
        label: 'Système',
        icon: 'sliders',
        iconOnly: false,
        permissions: [
          'Core.Privacy.Administrateur',
          'Core.Audit.Administrateur',
          'Core.Audit.Consultation',
          'Core.Features.Manage',
          'Core.Workflow.Manage',
          'Parcours.Demonstration.Administrateur',
          'Parcours.Demonstration.Concepteur',
          'Parcours.Demonstration.Démonstrateur',
          'Parcours.Demonstration.Observateur',
        ],
        items: [
          {
            permissions: ['Core.Privacy.Administrateur'],
            items: [
              { title: 'Confidentialité', icon: 'shield-halved', to: '/gestion/confidentialite/parametres' },
            ],
          },
          {
            permissions: ['Core.Audit.Administrateur', 'Core.Audit.Consultation'],
            items: [
              { title: "Journal d'audit", icon: 'clipboard-list', to: '/gestion/audit' },
            ],
          },
          {
            // The Fondation's own feature-flag screen. Parcours declares flags but had no way to
            // reach the page that toggles them. Every endpoint behind that screen requires
            // `Manage`, so the module alone would open a page that can only fail.
            module: 'Core.Features',
            permissions: ['Core.Features.Manage'],
            items: [
              { title: 'Fonctionnalités', icon: 'toggle-on', to: '/core/features' },
            ],
          },
          {
            permissions: ['Core.Workflow.Manage'],
            items: [
              { title: 'Flux de travail', icon: 'diagram-project', to: '/workflow', badge: 'beta' },
            ],
          },
          {
            // The four permissions open the page; it is the view that then decides
            // which gestures are offered there.
            permissions: [
              'Parcours.Demonstration.Administrateur',
              'Parcours.Demonstration.Concepteur',
              'Parcours.Demonstration.Démonstrateur',
              'Parcours.Demonstration.Observateur',
            ],
            items: [
              { title: 'Démonstration', icon: 'flask', to: '/gestion/demonstration', feature: DEMONSTRATION_FEATURE, },
            ],
          },
        ],
      },
      {
        // The management reports are all served by Parcours.LearningModule under
        // Administrateur or Superviseur; without one of the two, the page has nothing to offer.
        title: 'Rapports', icon: 'file-lines', to: '/gestion/rapports',
        module: 'Parcours.LearningModule',
        permissions: ['Parcours.LearningModule.Administrateur', 'Parcours.LearningModule.Superviseur'],
      }
    ],
  },
]);

/**
 * The runtime entries land after « Calendrier », looked up by its path rather than by a fixed
 * index: the dashboard took position 0 in #957, and an index would have slipped « Mon stage »
 * between the dashboard and the schedule. Falling back to the top only if the schedule ever
 * leaves the section.
 */
function afterScheduleIndex(groups: IMenuGroup[]): number {
  const scheduleIndex = groups.findIndex(group => group.to === STUDENT_SCHEDULE_PATH);
  return scheduleIndex === -1 ? 1 : scheduleIndex + 1;
}

export function setStudentStageMenuVisible(visible: boolean): void {
  const studentSection = menus.find(section => section.title === 'Élève');
  if (!studentSection) return;

  const existingIndex = studentSection.groups.findIndex(group => group.to === STUDENT_STAGE_PATH);
  if (visible && existingIndex === -1) {
    studentSection.groups.splice(afterScheduleIndex(studentSection.groups), 0, studentStageMenuGroup);
  } else if (!visible && existingIndex !== -1) {
    studentSection.groups.splice(existingIndex, 1);
  }
}

export function setStudentAssignmentsMenuVisible(visible: boolean): void {
  const studentSection = menus.find(section => section.title === 'Élève');
  if (!studentSection) return;

  const existingIndex = studentSection.groups.findIndex(group => group.to === STUDENT_ASSIGNMENTS_PATH);
  if (visible && existingIndex === -1) {
    const stageIndex = studentSection.groups.findIndex(group => group.to === STUDENT_STAGE_PATH);
    studentSection.groups.splice(
      stageIndex === -1 ? afterScheduleIndex(studentSection.groups) : stageIndex + 1,
      0,
      studentAssignmentsMenuGroup);
  } else if (!visible && existingIndex !== -1) {
    studentSection.groups.splice(existingIndex, 1);
  }
}
